import { post } from 'aws-amplify/api';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { useMutation } from 'react-query';
import { type QueryKeys, queryKeys } from '@/constants/query-key';
import { handleMaintenance } from '@/modules/utils/sign-out-utils';
import * as paths from '@/routes/paths';
import { removeSettingState } from '@/state/callbackHelpers';

export const apiPost = async <T>(apiName: string, path: string, options?: object) => {
  const authToken = (await fetchAuthSession()).tokens?.idToken?.toString();
  try {
    const { response } = post({
      apiName,
      path,
      options: {
        ...options,
        headers: {
          Authorization: authToken as string,
        },
      },
    });

    return await ((await response).body.json() as Promise<T>);
  } catch (error) {
    let statusCode = 0;

    if (error && typeof error === 'object' && 'response' in error) {
      const err = error as { response: { statusCode: number; body: string } };

      if (err.response?.statusCode) {
        statusCode = err.response.statusCode;
      }
      handleMaintenance(statusCode, err.response.body);
    }

    // // lambdaのタイムアウト、lambdaのメモリ超過
    if (statusCode === 502) {
      throw new Error('システムエラーが発生しました。時間を置いてリクエストしてください。');
    }

    if (statusCode === 403) {
      signOut()
        .then(() => {
          removeSettingState();
          window.location.replace(`/#${paths.login}`);
        })
        .catch(() => {});
    }

    throw new Error(error instanceof Error ? `${error.message} (${error?.name})` : '');
  }
};

export const useCreate = <T>(key: QueryKeys, apiName: string, path: string, options?: object) => {
  const res = useMutation<T, Error, Partial<T>>({
    mutationKey: queryKeys[key],
    mutationFn: async (postData) => apiPost<T>(apiName, path, { ...options, body: postData }),
  });

  return res;
};
